import React from "react";
import { BookOpen, Compass, Sparkles, Wrench } from "lucide-react";

import PageLayout from "@/components/layout/PageLayout";
import Seo from "@/components/foundation/Seo";
import Container from "@/components/foundation/Container";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import Badge from "@/components/foundation/Badge";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import GridPattern from "@/components/foundation/GridPattern";

import MerchantWaitlist from "@/components/marketing/MerchantWaitlist";

const CATEGORIES = [
  { icon: Compass, title: "Cash Flow Intelligence", body: "How to read cash flow signals like a modern underwriter." },
  { icon: BookOpen, title: "Funding Guides", body: "Playbooks for evaluating and applying for business capital." },
  { icon: Sparkles, title: "ZenScore Insights", body: "Deep dives on what moves your ZenScore — and why." },
  { icon: Wrench, title: "Product Updates", body: "Release notes, model updates, and platform announcements." },
];

export default function Resources() {
  return (
    <PageLayout>
      <Seo
        title="ZenMe Capital · Resources"
        description="Insights on cash flow, business funding, ZenScore intelligence, and modern underwriting. New resources publishing soon — join the list."
        path="/resources"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-28 pb-12 sm:pt-32 sm:pb-16"
        data-testid="resources-hero"
      >
        <GridPattern variant="dots" size={28} opacity={0.4} />
        <GlowOrb color="emerald" size={460} intensity={0.18} className="-bottom-40 -left-40" />

        <Container className="relative z-10">
          <div className="max-w-3xl">
            <FadeIn>
              <Badge variant="emerald" pulse>Resources</Badge>
            </FadeIn>
            <FadeIn delay={0.06}>
              <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Financial intelligence,{" "}
                <span className="text-brand-emerald">translated.</span>
              </h1>
            </FadeIn>
            <FadeIn delay={0.14}>
              <p className="mt-6 max-w-xl text-base text-text-secondary sm:text-lg leading-relaxed">
                Insights on cash flow, business funding, and modern
                underwriting — coming soon.
              </p>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* EMPTY STATE + WAITLIST */}
      <Section spacing="md">
        <FadeIn>
          <Card
            variant="glass"
            interactive={false}
            className="mx-auto flex max-w-2xl flex-col items-center gap-6 p-10 text-center sm:p-14"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-brand-emerald/25 bg-brand-emerald/10 text-brand-emerald">
              <BookOpen className="h-7 w-7" strokeWidth={1.75} />
            </div>
            <h2 className="font-display text-3xl font-semibold text-white sm:text-4xl">
              First posts publishing soon.
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-text-secondary sm:text-base">
              Join the list to get notified when new resources drop — deep
              dives, playbooks, and product updates.
            </p>
            <div className="mt-2 w-full">
              <MerchantWaitlist
                size="md"
                source="resources_waitlist"
                data-testid="resources-waitlist"
                ctaLabel="Notify Me"
              />
            </div>
          </Card>
        </FadeIn>
      </Section>

      {/* CATEGORY GRID */}
      <Section
        eyebrow="Coming Categories"
        headline="What we'll be publishing."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {CATEGORIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <FadeIn key={c.title} delay={0.06 * i}>
                <Card
                  variant="elevated"
                  className="group relative flex h-full flex-col gap-4 p-8"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-brand-emerald/25 bg-brand-emerald/10 text-brand-emerald transition-transform duration-300 group-hover:-translate-y-0.5">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <Badge variant="soft">Coming Soon</Badge>
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">
                      {c.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                      {c.body}
                    </p>
                  </div>
                </Card>
              </FadeIn>
            );
          })}
        </div>
      </Section>
    </PageLayout>
  );
}
